package com.dotpx.ambient

import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

/**
 * Allocation-free sine oscillator. Frequency changes only replace the complex rotation;
 * samples advance through a recurrence and periodically snap back to the tracked phase.
 */
internal class RecursiveSineOscillator(private val sampleRate: Double) {
    private var phase = 0.0
    private var phaseStep = 0.0
    private var rotationSin = 0.0
    private var rotationCos = 1.0
    private var sine = 0.0
    private var cosine = 1.0
    private var framesUntilCorrection = CORRECTION_INTERVAL_FRAMES

    val phaseRadians: Double
        get() = phase
    val sineValue: Float
        get() = sine.toFloat()
    val cosineValue: Float
        get() = cosine.toFloat()

    init {
        require(sampleRate.isFinite() && sampleRate > 0.0) { "sampleRate must be finite and positive" }
    }

    fun reset(phaseRadians: Double, frequency: Double) {
        phase = wrapPhase(phaseRadians)
        sine = sin(phase)
        cosine = cos(phase)
        framesUntilCorrection = CORRECTION_INTERVAL_FRAMES
        setFrequency(frequency)
    }

    fun setFrequency(frequency: Double) {
        val limitedFrequency = frequency.coerceIn(0.0, sampleRate * 0.45)
        phaseStep = 2.0 * PI * limitedFrequency / sampleRate
        rotationSin = sin(phaseStep)
        rotationCos = cos(phaseStep)
    }

    fun next(): Float {
        phase = wrapPhase(phase + phaseStep)
        framesUntilCorrection -= 1
        if (framesUntilCorrection <= 0) {
            sine = sin(phase)
            cosine = cos(phase)
            framesUntilCorrection = CORRECTION_INTERVAL_FRAMES
        } else {
            val nextSine = sine * rotationCos + cosine * rotationSin
            cosine = cosine * rotationCos - sine * rotationSin
            sine = nextSine
        }
        return sine.toFloat()
    }

    private fun wrapPhase(value: Double): Double = when {
        value >= PI * 2.0 -> value - PI * 2.0
        value < 0.0 -> value + PI * 2.0
        else -> value
    }

    private companion object {
        const val CORRECTION_INTERVAL_FRAMES = 4_096
    }
}
